import * as React from 'react';
import { useOptionalTenantBranding } from '@/branding/TenantBrandingProvider';
import { defaultBranding } from '@/branding/defaults';
import {
  acquireRouteTitleOwnership,
  formatDocumentTitle,
  releaseRouteTitleOwnership,
  setCanonicalLink,
  setDocumentTitle,
  setMetaDescription,
} from './document-meta';

/**
 * Per-route document metadata (#1716).
 *
 * The SPA shipped one static `<title>` + no per-route
 * `<meta name="description">`, so every route scored an identical
 * Lighthouse SEO 83. `useRouteMeta` / `<RouteMeta>` let a page set its
 * own `title`, `description`, and (optionally) canonical URL; the effect
 * updates the document head in place (never appending duplicate tags)
 * and reverts to the branded product-name-only title on unmount so a
 * route that sets no metadata doesn't inherit the previous page's.
 *
 * Titles are formatted `<page> · <product_name>` (product name comes
 * from tenant branding). Pass already-translated strings — call
 * `t('seo.<page>.title')` at the call site so the values stay in the
 * i18n catalog (en + nb + es parity).
 */
export interface RouteMetaOptions {
  /** Page-specific title segment, e.g. `t('seo.login.title')`. */
  readonly title?: string;
  /** `<meta name="description">` text, e.g. `t('seo.login.description')`. */
  readonly description?: string;
  /**
   * Canonical URL. When `true`, the current `location.origin + pathname`
   * is used; pass a string to override (e.g. an absolute marketing URL).
   * Omit to leave any existing canonical untouched.
   */
  readonly canonical?: string | boolean;
}

export function useRouteMeta(options: RouteMetaOptions): void {
  // Provider-agnostic: fall back to the platform defaults when no
  // <TenantBrandingProvider> is mounted (isolated page unit tests render
  // pages bare). In the app the provider always wraps the router.
  const ctx = useOptionalTenantBranding();
  const branding = ctx?.branding ?? defaultBranding;
  const productName = branding.product_name || 'Senviro';
  const { title, description, canonical } = options;

  // Claim title ownership for this hook's whole lifetime so the fallback
  // `<DocumentTitle>` in AppShell (whose effect runs after this page's in
  // commit order) doesn't clobber the route title back to the bare
  // product name. Released on unmount.
  React.useEffect(() => {
    acquireRouteTitleOwnership();
    return () => {
      releaseRouteTitleOwnership();
    };
  }, []);

  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    setDocumentTitle(formatDocumentTitle(title, productName));
    if (description !== undefined) setMetaDescription(description);

    if (canonical !== undefined) {
      const href =
        canonical === true
          ? typeof window !== 'undefined'
            ? `${window.location.origin}${window.location.pathname}`
            : ''
          : canonical === false
            ? ''
            : canonical;
      setCanonicalLink(href);
    }

    return () => {
      // Revert to the branded product-name title and drop the managed
      // description/canonical so the next route starts clean. The next
      // route's own RouteMeta (or DocumentTitle) re-sets them.
      setDocumentTitle(productName);
      if (description !== undefined) setMetaDescription('');
      if (canonical !== undefined) setCanonicalLink('');
    };
  }, [title, description, canonical, productName]);
}

/**
 * Declarative wrapper around {@link useRouteMeta}. Render anywhere in a
 * page body: `<RouteMeta title={t('seo.buildings.title')} … />`. Renders
 * nothing.
 */
export function RouteMeta(props: RouteMetaOptions): null {
  useRouteMeta(props);
  return null;
}
